import Button from 'antd/lib/button';
import Skeleton from 'antd/lib/skeleton';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import * as React from 'react';
import {
    AppPlatform,
    AppStoreType,
    AutomationType,
    FeatureFlag,
    FeatureFlagTimeSeriesDatum,
    MonitoringMetricHealthStatus,
    StabilityMetricType,
    SubscriptionFeatureType,
    UserRatingsMetricType,
} from '@/types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { ChartColors, useChartColors, useChartTheme } from '../styles/themeMode';
import { ArrowRightOutlined, FlagFilled, InfoCircleOutlined, SwapOutlined, WarningFilled } from '@ant-design/icons';

import { GroupedMonitoringStatistic, MonitoringStatisticGroup } from '@/typescriptHelpers';
import { Area, AreaConfig } from '@ant-design/plots';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '@/timeHelpers';
import { forIn, groupBy, isNil, max, min } from 'lodash';
import { Annotation } from '@antv/g2plot';
import autoHaltIcon from '../../../assets/autoHaltIcon.png';
import autoAccelerateIcon from '../../../assets/autoAccelerateIcon.png';
import { generateAutomationAlertIcon, generateAutomationThunderboltIcon } from './monitoringHelpers';
import {
    displayFormattedMonitoringMetricValue,
    phasedReleasePausedDisplayTextForAppStoreType,
} from '@/helpers/displayHelpers';
import {
    decimalPlacesForStatValue,
    limitsPerUnit,
    specificUnitForMetricType,
    statTypeForMonitoringMetricType,
    unitForMonitoringMetricType,
} from '@/helpers';
import ReactDOM from 'react-dom';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import Popover from 'antd/lib/popover';
import Descriptions from 'antd/lib/descriptions';
import IntegrationIcon from '../icons/IntegrationIcon';

const formatPastDate = (timestamp: string) => {
    return dayjs(timestamp).local().calendar(null, {
        sameDay: '[Today]',
        lastDay: DatetimeFormat.ShortMonthDate,
        lastWeek: DatetimeFormat.ShortMonthDate,
        nextDay: DatetimeFormat.ShortMonthDate,
        nextWeek: DatetimeFormat.ShortMonthDate,
        sameElse: DatetimeFormat.ShortMonthDate,
    });
};

const formatRecentDate = (timestamp: string) => {
    return dayjs.utc(timestamp).local().format(DatetimeFormat.Hour);
};

const dropTimezone = (timestamp: string) => {
    return dayjs.utc(timestamp).format('YYYY-MM-DDTHH:mm:ss');
};

const generateGraphConfig: (
    args: MonitoringStatisticGroup,
    featureFlags: FeatureFlagTimeSeriesDatum[],
    releasedAt: string,
    appStoreType: AppStoreType,
    chartColors: ChartColors,
    shouldShowDisabledUI?: boolean,
    onShowFeatureFlag?: (flag: FeatureFlag) => void,
    onPopoverOpen?: () => void,
) => AreaConfig = (
    {
        dataPoints,
        threshold,
        isMoreBetter,
        type,
        status,
        hasRecentDataOnly,
        automationUsage,
        currentValue,
        additionalDataSeries,
    },
    featureFlags,
    releasedAt,
    appStoreType,
    chartColors,
    shouldShowDisabledUI,
    onShowFeatureFlag,
    onPopoverOpen,
) => {
    const aboveThresholdColor = isMoreBetter ? runwayColors.statusGreen : runwayColors.statusRed;
    const belowThresholdColor = isMoreBetter ? runwayColors.statusRed : runwayColors.statusGreen;

    const releasedAtMoment = dayjs.utc(releasedAt);
    const releasedAtTimestamp =
        hasRecentDataOnly ?
            releasedAtMoment.valueOf()
        :   dayjs(dropTimezone(releasedAtMoment.startOf('day').toISOString())).valueOf();

    let annotations: Annotation[] = [];

    const triggeredAccelerateAutomationAt = automationUsage[AutomationType.AutoPromoteStablePhasedReleases].triggeredAt;
    const triggeredHaltAutomationAt = automationUsage[AutomationType.AutoHaltUnstablePhasedReleases].triggeredAt;

    const mappedDataPoints =
        dataPoints?.map((data) => {
            // non recent data has day granularity, ignore the time part
            const day = dayjs(dropTimezone(data.timestamp));
            const timestamp = day.valueOf();

            const normalizedValue = data.value - threshold;

            const decimalPlaces = decimalPlacesForStatValue(
                unitForMonitoringMetricType(type),
                normalizedValue,
                statTypeForMonitoringMetricType(type),
            );

            const value = Number(normalizedValue.toFixed(decimalPlaces ?? 0));
            return {
                timestamp,
                value,
            };
        }) ?? [];

    // if there is not enough data, don't show the graph
    if (mappedDataPoints.length < 1 || isNil(currentValue)) {
        return null;
    }

    if (mappedDataPoints.length === 1) {
        // if there is only one data point, add another one to make the graph look like a line
        mappedDataPoints.push({
            timestamp: releasedAtTimestamp - 10000,
            value: mappedDataPoints[0].value,
        });
    }

    mappedDataPoints.sort((a, b) => a.timestamp - b.timestamp);

    const allDataPoints = mappedDataPoints;
    const hasAdditionalSeries = additionalDataSeries && Object.keys(additionalDataSeries).length > 0;

    const sampleValues = mappedDataPoints.map((dataPoint) => dataPoint.value);

    const lastDataPoint = mappedDataPoints[mappedDataPoints.length - 1];
    const firstDataPoint = mappedDataPoints[0];

    let color = chartColors.axisLabel;

    if (status === MonitoringMetricHealthStatus.Healthy) {
        color = runwayColors.statusGreen;
    } else if (status === MonitoringMetricHealthStatus.Unhealthy) {
        color = runwayColors.statusRed;
    }

    annotations.push({
        type: 'dataMarker',
        top: true,
        line: null,
        position: [lastDataPoint.timestamp, lastDataPoint.value],
        point: {
            style: {
                stroke: chartColors.tooltipText,
                r: 8,
                lineWidth: 4,
                fill: color,
            },
        },
    });

    // annotations for feature flags

    let ffAnnotations = [];

    if (hasRecentDataOnly) {
        ffAnnotations = featureFlags.map((ff) => {
            return {
                position: [dayjs.utc(ff.timestamp).valueOf(), 'max'],
                type: 'html',
                offsetY: -36,
                html: () => {
                    const node = document.createElement('div');
                    ReactDOM.render(
                        <div>
                            <div
                                style={{
                                    height: 122,
                                    opacity: 1,
                                    display: 'flex',
                                    alignItems: 'stretch',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    gap: 5,
                                }}
                            >
                                <FeatureFlagPopover
                                    onShowFeatureFlag={onShowFeatureFlag}
                                    datums={[ff]}
                                    onPopoverOpen={onPopoverOpen}
                                />
                                <div
                                    style={{
                                        width: 2,
                                        borderLeft: `2px dashed ${runwayColors.primary}`,
                                        flexGrow: 1,
                                        opacity: 0.4,
                                    }}
                                />
                            </div>
                        </div>,
                        node,
                    );
                    return node;
                },
            } as Annotation;
        });
    } else {
        // long-range data has day-level granularity, so we group all feature flag changes within a single day
        const featureFlagDataPointsGroupedByDay = groupBy(featureFlags, (flag) =>
            dayjs.utc(flag.timestamp).startOf('d').valueOf(),
        );

        forIn(featureFlagDataPointsGroupedByDay, (groupedByDayFlagChanges, dayKey) => {
            const batchedDate = dayjs(Number(dayKey)).toISOString();

            const annotation = {
                position: [Number(dayKey), 'max'],
                type: 'html',
                offsetY: -36,
                html: () => {
                    const node = document.createElement('div');
                    ReactDOM.render(
                        <div>
                            <div
                                style={{
                                    height: 122,
                                    opacity: 1,
                                    display: 'flex',
                                    alignItems: 'stretch',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    gap: 5,
                                }}
                            >
                                <FeatureFlagPopover
                                    onShowFeatureFlag={onShowFeatureFlag}
                                    datums={groupedByDayFlagChanges}
                                    batchedDay={batchedDate}
                                    onPopoverOpen={onPopoverOpen}
                                />
                                <div
                                    style={{
                                        width: 2,
                                        borderLeft: `2px dashed ${runwayColors.primary}`,
                                        flexGrow: 1,
                                        opacity: 0.4,
                                    }}
                                />
                            </div>
                        </div>,
                        node,
                    );
                    return node;
                },
            } as Annotation;

            ffAnnotations.push(annotation);
        });
    }

    annotations = annotations.concat(ffAnnotations);

    // normalize data
    const minValue = min(sampleValues);
    const maxValue = max(sampleValues);

    let axisGap = Math.abs(minValue - maxValue) * 0.15;

    let minYAxis = minValue - axisGap;
    let maxYAxis = maxValue + axisGap;
    if (axisGap === 0) {
        // default case
        axisGap = Math.abs(minValue) * 0.5;
        minYAxis = minValue - axisGap;
        maxYAxis = maxValue + axisGap;

        const anyValue = minValue; // min value is the same as all values in this case
        const [minLimit, maxLimit] = limitsPerUnit(unitForMonitoringMetricType(type));
        if (minLimit !== null && maxLimit !== null) {
            const adjustedMinLimit = minLimit - threshold;
            const adjustedMaxLimit = maxLimit - threshold;
            if (anyValue === adjustedMinLimit) {
                minYAxis = adjustedMinLimit;
            } else if (anyValue === adjustedMaxLimit) {
                maxYAxis = adjustedMaxLimit;
            }
        }
    }

    // extend y-axis bounds to include additional series data
    if (hasAdditionalSeries) {
        Object.values(additionalDataSeries).forEach((points) => {
            points.forEach((p) => {
                sampleValues.push(Number((p.value - (threshold ?? 0)).toFixed(2)));
            });
        });
        const allMin = min(sampleValues);
        const allMax = max(sampleValues);
        let allGap = Math.abs(allMin - allMax) * 0.15;
        if (allGap === 0) {
            allGap = Math.abs(allMin) * 0.5;
        }
        minYAxis = allMin - allGap;
        maxYAxis = allMax + allGap;
    }

    const ONE_HOUR = 60 * 60 * 1000;
    const firstDataPointTimestamp = firstDataPoint.timestamp;
    const minXAxis = Math.min(releasedAtTimestamp, firstDataPointTimestamp);
    const lastDataPointTimestamp = lastDataPoint.timestamp;
    const maxXAxis = lastDataPointTimestamp;

    // add releasedAtTimestamp as a reference line
    const releasedAtHoverAreaWidth = 20;

    annotations.push({
        type: 'html',
        top: true,
        alignX: 'left',
        offsetY: -36,
        offsetX: -releasedAtHoverAreaWidth / 2,
        html: () => {
            const node = document.createElement('div');
            ReactDOM.render(
                <Tooltip
                    title={
                        <Space direction="vertical" size={0}>
                            <Typography.Text
                                style={{
                                    color: antGrey.grey6,
                                }}
                            >
                                Released on:
                            </Typography.Text>
                            <Typography.Text
                                style={{
                                    color: 'white',
                                }}
                            >
                                {dayjs.utc(releasedAt).local().format(DatetimeFormat.ShortMonthDateTime2)}
                            </Typography.Text>
                        </Space>
                    }
                >
                    <div
                        style={{
                            height: 122,
                            width: releasedAtHoverAreaWidth,
                            opacity: 1,
                            display: 'flex',
                            alignItems: 'stretch',
                            justifyContent: 'center',
                        }}
                    >
                        <div
                            style={{
                                width: 2,
                                borderLeft: `2px dashed ${antGrey.grey6}`,
                            }}
                        />
                    </div>
                </Tooltip>,
                node,
            );
            return node;
        },
        position: [releasedAtTimestamp, 'max'],
    });

    // add threshold annotation if it exists
    const hasThreshold = !isNil(threshold);
    if (hasThreshold) {
        const constrainedYValue = Math.min(Math.max(0, minYAxis), maxYAxis);

        annotations.push({
            type: 'line',
            start: [minXAxis - minXAxis * 0.01, constrainedYValue],
            offsetX: -5,
            end: ['end', constrainedYValue],
            style: {
                stroke: chartColors.axisLabel,
                lineDash: [4, 4],
                lineWidth: 2,
            },
        });

        // above threshold area
        if (maxValue >= 0) {
            annotations.push({
                top: false,
                type: 'regionFilter',
                start: ['start', constrainedYValue],
                end: ['end', 'end'],
                color: aboveThresholdColor,
            });
        }

        // below threshold area
        if (minValue <= 0) {
            annotations.push({
                top: false,
                type: 'regionFilter',
                start: ['start', 'start'],
                end: ['end', constrainedYValue],
                color: belowThresholdColor,
            });
        }

        // If the metric triggered an automation, add an annotation for that, it should be rendered
        // on the moment the automation was triggered
        function generateImageAnnotation(
            triggeredAtString: string,
            isAutoHalt: boolean,
            yPosition: number,
            appStoreType: AppStoreType,
        ) {
            const imageSize = 24;
            const triggeredAtDate = dayjs.utc(triggeredAtString);
            const triggeredAt = triggeredAtDate.valueOf();

            const image = isAutoHalt ? autoHaltIcon : autoAccelerateIcon;

            let text = `Runway accelerated this release to 100% of users on:`;
            if (isAutoHalt) {
                text = `Runway ${phasedReleasePausedDisplayTextForAppStoreType(appStoreType).toLowerCase()} this release on:`;
            }

            const triggeredAtFormatted = triggeredAtDate.local().format(DatetimeFormat.ShortMonthDateTime);

            return {
                type: 'html',
                top: true,
                alignX: 'left',
                alignY: 'middle',
                offsetX: -12,
                offsetY: -12,
                html: () => {
                    const node = document.createElement('div');
                    ReactDOM.render(
                        <Tooltip
                            showArrow={false}
                            title={
                                <Space direction="vertical" size={0}>
                                    <Typography.Text
                                        style={{
                                            color: antGrey.grey6,
                                        }}
                                    >
                                        {text}
                                        <br />
                                        <span
                                            style={{
                                                color: 'white',
                                            }}
                                        >
                                            {triggeredAtFormatted}
                                        </span>
                                    </Typography.Text>
                                </Space>
                            }
                        >
                            <img
                                src={image}
                                style={{
                                    width: imageSize,
                                    height: imageSize,
                                }}
                            />
                        </Tooltip>,
                        node,
                    );
                    return node;
                },
                position: [triggeredAt, yPosition],
            } as Annotation;
        }

        annotations.push({
            type: 'html',
            top: true,
            alignX: 'left',
            alignY: 'middle',
            offsetX: 17,
            offsetY: -12,
            html: () => {
                const node = document.createElement('div');
                ReactDOM.render(
                    <Tooltip
                        showArrow={false}
                        title={
                            <Space direction="vertical" size={0}>
                                <Typography.Text
                                    style={{
                                        color: antGrey.grey6,
                                    }}
                                >
                                    Threshold:
                                </Typography.Text>
                                <Typography.Text
                                    style={{
                                        color: 'white',
                                    }}
                                >
                                    {displayFormattedMonitoringMetricValue(type, threshold)}
                                </Typography.Text>
                            </Space>
                        }
                    >
                        <WarningFilled style={{ color: antGrey.grey7, fontSize: 22 }} />
                    </Tooltip>,
                    node,
                );
                return node;
            },
            position: ['max', constrainedYValue],
        });

        const calculateIntersectionPoint = (triggeredAt: string) => {
            const triggeredAtTimestamp = dayjs.utc(triggeredAt).valueOf();

            let triggeredAtDataPoint = null;
            let previousDataPoint = null;

            for (const dataPoint of mappedDataPoints) {
                if (dataPoint.timestamp >= triggeredAtTimestamp) {
                    triggeredAtDataPoint = dataPoint;
                    break;
                }
                previousDataPoint = dataPoint;
            }

            if (triggeredAtDataPoint) {
                let finalPointValue = triggeredAtDataPoint.value;

                if (previousDataPoint) {
                    const slope =
                        (triggeredAtDataPoint.value - previousDataPoint.value) /
                        (triggeredAtDataPoint.timestamp - previousDataPoint.timestamp);

                    finalPointValue =
                        slope * (triggeredAtTimestamp - triggeredAtDataPoint.timestamp) + triggeredAtDataPoint.value;
                }

                // midpoint between the two data points
                return finalPointValue;
            }

            return null;
        };

        if (triggeredAccelerateAutomationAt && !isNil(threshold)) {
            const finalDataPoint = calculateIntersectionPoint(triggeredAccelerateAutomationAt);

            if (finalDataPoint) {
                annotations.push(
                    generateImageAnnotation(triggeredAccelerateAutomationAt, false, finalDataPoint, appStoreType),
                );
            }
        }

        if (triggeredHaltAutomationAt && !isNil(threshold)) {
            const finalDataPoint = calculateIntersectionPoint(triggeredHaltAutomationAt);

            if (finalDataPoint) {
                annotations.push(
                    generateImageAnnotation(triggeredHaltAutomationAt, true, constrainedYValue, appStoreType),
                );
            }
        }
    }

    if (shouldShowDisabledUI) {
        // clear the annotations array if we are to show disabled UI
        annotations.length = 0;
    }

    const isAdoptionRate = type === StabilityMetricType.AdoptionRate;
    const rolloutStartDay = dayjs(allDataPoints[0].timestamp).startOf('day');

    const allDataPointsForGraph =
        isAdoptionRate ?
            allDataPoints.map((p) => {
                const day = dayjs(p.timestamp).startOf('day');
                const dayIndex = day.diff(rolloutStartDay, 'day') + 1;
                return { ...p, dayIndex, timestamp: p.timestamp };
            })
        :   allDataPoints;

    const maxDayIndex = Math.max(...allDataPointsForGraph.map((p) => p['dayIndex']));
    let finalData = allDataPointsForGraph.map((p) => ({ ...p, seriesName: 'Current release adoption' }));

    if (hasAdditionalSeries) {
        Object.entries(additionalDataSeries).forEach(([label, points]) => {
            const mappedPoints = points.map((p) => {
                const normalizedValue = Number((p.value - (threshold ?? 0)).toFixed(2));

                if (isAdoptionRate) {
                    const day = dayjs(p.timestamp).startOf('day');
                    const dayIndex = day.diff(rolloutStartDay, 'day') + 1;
                    return {
                        dayIndex,
                        timestamp: p.timestamp,
                        value: normalizedValue,
                        seriesName: label,
                    };
                } else {
                    return {
                        timestamp: dayjs(p.timestamp).valueOf(),
                        value: normalizedValue,
                        seriesName: label,
                    };
                }
            });
            finalData = finalData.concat(mappedPoints as any);
        });
    }

    const styleCurrentLine = {
        lineWidth: 4,
        stroke: chartColors.axisLabel,
        lineCap: 'round',
        lineJoin: 'round',
    };

    const styleAdditionalLine = {
        lineWidth: 4,
        stroke: chartColors.secondarySeries,
        lineDash: [4, 4],
        strokeOpacity: 0.7,
    };

    const areaCurrentLineStyle = {
        fillOpacity: hasThreshold ? 0.4 : 0.1,
        fill: hasThreshold ? chartColors.tooltipText : chartColors.axisLabel,
    };

    const BASE_GRAPH_CONFIG: AreaConfig = {
        xField: isAdoptionRate ? 'dayIndex' : 'timestamp',
        yField: 'value',
        seriesField: hasAdditionalSeries ? 'seriesName' : undefined,
        animation: false,
        isStack: false,
        data: shouldShowDisabledUI ? [] : finalData,
        renderer: 'canvas',
        legend: false,
        meta: {
            timestamp: {
                type: 'time',
                formatter: (value) => {
                    const formattedDate = hasRecentDataOnly ? formatRecentDate(value) : formatPastDate(value);
                    return shouldShowDisabledUI ? '' : formattedDate;
                },
            },
            dayIndex: {
                type: 'linear',
                formatter: (v) => `Day ${Number(v)}`,
            },
            value: {
                type: 'linear',
                formatter: (value) =>
                    displayFormattedMonitoringMetricValue(
                        type,
                        value + threshold, // compensate for the -threshold above
                    ),
            },
        },
        color: hasAdditionalSeries ? [chartColors.axisLabel, chartColors.secondarySeries] : chartColors.axisLabel,
        padding: [10, 24, 20, 44],
        yAxis: {
            showLast: true,
            label: null,
            grid: null,
            range: [hasThreshold ? 0.2 : 0, 0.7],
            min: minYAxis,
            max: maxYAxis,
            maxLimit: maxYAxis,
            minLimit: minYAxis,
            nice: true,
        },
        tooltip: {
            fields: ['timestamp', 'value', 'seriesName', 'dayIndex'],
            showMarkers: true,
            showTitle: isAdoptionRate,
            domStyles: {
                'g2-tooltip': {
                    backgroundColor: chartColors.tooltipBg,
                    color: chartColors.tooltipText,
                },
            },
            formatter: (datum) => {
                const { timestamp, value, seriesName } = datum;
                const momentTimestamp = dayjs.utc(timestamp).local();
                let displayName =
                    hasRecentDataOnly ?
                        momentTimestamp.format(DatetimeFormat.ShortMonthDateTime2)
                    :   momentTimestamp.format(DatetimeFormat.ShortMonthDateYear);
                if (isAdoptionRate) displayName = `${seriesName}`;
                return {
                    name: displayName,
                    value: displayFormattedMonitoringMetricValue(type, value + threshold),
                };
            },
            shared: isAdoptionRate,
            showCrosshairs: !isAdoptionRate,
            title: (dayIndex) => (isAdoptionRate ? `${dayIndex}` : ''),
        },
        annotations,
        xAxis: {
            range: [0, 0.95],
            showLast: false,
            line: null,
            nice: !isAdoptionRate,
            min: isAdoptionRate ? 1 : minXAxis,
            max: isAdoptionRate ? maxDayIndex : maxXAxis,
            tickInterval:
                isAdoptionRate ? 1
                : hasRecentDataOnly ? ONE_HOUR
                : ONE_HOUR * 24,
            tickLine: {
                style: {
                    stroke: chartColors.gridLine,
                    lineWidth: 2,
                },
            },
            grid: {
                line: {
                    style: {
                        stroke: chartColors.gridLine,
                        lineWidth: 2,
                    },
                },
            },
        },

        line: {
            style: (datum: any) => {
                if (hasAdditionalSeries) {
                    return datum.seriesName === finalData[0]?.seriesName ? styleCurrentLine : styleAdditionalLine;
                }
                return styleCurrentLine;
            },
        },
        areaStyle: (datum: any) => {
            if (hasAdditionalSeries) {
                return datum.seriesName === finalData[0]?.seriesName ? areaCurrentLineStyle : { fillOpacity: 0 };
            }
            return areaCurrentLineStyle;
        },
    };

    return BASE_GRAPH_CONFIG;
};

export interface MonitoringStatCardBaseProps extends GroupedMonitoringStatistic {
    isLoading?: boolean;
    shouldShowAlert: boolean;
    releasedAt: string;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    disableLoadShimmer?: boolean;
    shouldApplyRestrictedTooltipForRollouts?: boolean;
    shouldShowDisabledUI?: boolean;
    featureFlags: FeatureFlagTimeSeriesDatum[];
    onShowFeatureFlag?: (flag: FeatureFlag) => void;
    onInspectFeatureFlag?: (chartName: string) => void;
}

export const MonitoringStatCardBase: React.FC<MonitoringStatCardBaseProps> = ({
    title,
    groups,
    integrationType,
    isLoading,
    shouldShowAlert,
    releasedAt,
    platform,
    appStoreType,
    disableLoadShimmer,
    shouldApplyRestrictedTooltipForRollouts,
    shouldShowDisabledUI,
    featureFlags,
    onShowFeatureFlag,
    onInspectFeatureFlag,
}) => {
    const chartTheme = useChartTheme();
    const chartColors = useChartColors();
    const preferredIndex = groups.findIndex((group) => !isNil(group.threshold));

    const [selectedDataPointsGroupIndex, setSelectedDataPointsGroupIndex] = React.useState(
        preferredIndex !== -1 ? preferredIndex : 0,
    );

    const selectedDataPointsGroup = groups[selectedDataPointsGroupIndex];

    const isUsedForAutoAccelerate = groups.some((predicate) => {
        return predicate.automationUsage[AutomationType.AutoPromoteStablePhasedReleases].isUsed;
    });

    const isUsedForAutoHalt = groups.some((predicate) => {
        return predicate.automationUsage[AutomationType.AutoHaltUnstablePhasedReleases].isUsed;
    });

    const isUserReviews = groups.some((group) =>
        Object.values(UserRatingsMetricType).includes(group.type as UserRatingsMetricType),
    );

    const hasMultipleUnits = groups.length > 1;

    const handlePopoverOpen = React.useCallback(() => {
        onInspectFeatureFlag?.(title);
    }, [onInspectFeatureFlag, title]);

    const graphConfig = React.useMemo(() => {
        if (!selectedDataPointsGroup) {
            return null;
        }

        return generateGraphConfig(
            selectedDataPointsGroup,
            featureFlags,
            releasedAt,
            appStoreType,
            chartColors,
            shouldShowDisabledUI,
            onShowFeatureFlag,
            handlePopoverOpen,
        );
    }, [selectedDataPointsGroup, featureFlags, releasedAt, platform, chartColors, handlePopoverOpen]);

    return (
        <div
            style={{
                backgroundColor: antGrey.grey2,
                border: `1px solid ${antGrey.grey4}`,
                borderRadius: 8,
                position: 'relative',
                minWidth: 380,
                height: 256,
                overflow: 'hidden',
            }}
        >
            <Space
                direction="vertical"
                style={{
                    padding: 20,
                    width: '100%',
                }}
            >
                {isLoading ?
                    <Skeleton
                        active={!disableLoadShimmer}
                        round={false}
                        style={{
                            padding: 12,
                        }}
                        paragraph={{
                            rows: 3,
                        }}
                    />
                :   <>
                        <div
                            style={{
                                position: 'relative',
                                zIndex: 2,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    position: 'relative',
                                }}
                            >
                                <Space>
                                    {generateAutomationThunderboltIcon({
                                        isUsedForAutoAccelerate,
                                        isUsedForAutoHalt,
                                        prefix: 'This metric is being used for the',
                                    })}

                                    {shouldShowAlert && generateAutomationAlertIcon()}
                                    {shouldApplyRestrictedTooltipForRollouts ?
                                        <PlanRestrictedFeatureWrapper
                                            feature={SubscriptionFeatureType.Rollouts}
                                            requiresTooltip
                                        >
                                            <Typography.Text
                                                style={{
                                                    color: antGrey.grey8,
                                                }}
                                            >
                                                <strong>{title}</strong>
                                            </Typography.Text>
                                        </PlanRestrictedFeatureWrapper>
                                    :   <Typography.Text
                                            style={{
                                                color: antGrey.grey8,
                                            }}
                                        >
                                            <strong>{title}</strong>
                                        </Typography.Text>
                                    }
                                    {isUserReviews && (
                                        <Tooltip title="This metric represents the average of the written customer reviews for the current version only.">
                                            <InfoCircleOutlined
                                                style={{
                                                    color: runwayColors.info,
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </Space>
                                <IntegrationIcon integrationId={integrationType} size={20} marginTop={2} />
                            </div>
                            {shouldShowDisabledUI ?
                                <div
                                    style={{
                                        backgroundColor: 'white',
                                        padding: 8,
                                        marginTop: 16,
                                        borderRadius: 4,
                                    }}
                                >
                                    <Typography.Text
                                        style={{
                                            color: antGrey.grey7,
                                            fontSize: 12,
                                        }}
                                    >
                                        User ratings metrics not available on Free plan.
                                    </Typography.Text>
                                </div>
                            :   <Space
                                    align={hasMultipleUnits ? 'center' : 'baseline'}
                                    style={{
                                        marginTop: 10,
                                    }}
                                >
                                    <Typography.Text
                                        style={{
                                            color: antGrey.grey8,
                                            fontSize: 30,
                                        }}
                                    >
                                        {selectedDataPointsGroup.value}
                                    </Typography.Text>
                                    {hasMultipleUnits ?
                                        <Button
                                            icon={<SwapOutlined />}
                                            size="small"
                                            type="dashed"
                                            style={{
                                                fontSize: 12,
                                                borderRadius: 2,
                                                color: runwayColors.primary,
                                            }}
                                            onClick={() => {
                                                setSelectedDataPointsGroupIndex(
                                                    (selectedDataPointsGroupIndex + 1) % groups.length, // rotate through the units
                                                );
                                            }}
                                        >
                                            {selectedDataPointsGroup?.unit}{' '}
                                            {specificUnitForMetricType(selectedDataPointsGroup?.type)}
                                        </Button>
                                    :   <Typography.Text
                                            style={{
                                                color: antGrey.grey6,
                                                fontSize: 12,
                                            }}
                                        >
                                            {selectedDataPointsGroup?.unit}
                                        </Typography.Text>
                                    }
                                </Space>
                            }
                        </div>

                        <div
                            style={{
                                height: shouldShowDisabledUI ? '55%' : '60%',
                                width: '100%',
                                position: 'absolute',
                                zIndex: 100,
                                left: 0,
                                bottom: 10,
                            }}
                        >
                            {graphConfig ?
                                <Area
                                    {...graphConfig}
                                    theme={chartTheme}
                                    style={{
                                        height: '100%',
                                        width: '100%',
                                    }}
                                />
                            :   <div
                                    style={{
                                        display: 'flex',
                                        width: '100%',
                                        height: '100%',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography.Text
                                        style={{
                                            color: antGrey.grey6,
                                        }}
                                    >
                                        Not enough data.
                                    </Typography.Text>
                                </div>
                            }
                        </div>
                    </>
                }
            </Space>
        </div>
    );
};

export const MonitoringStatLoadingCard: React.FC<Partial<MonitoringStatCardBaseProps>> = (props) => {
    return (
        <MonitoringStatCardBase
            isLoading
            key={''}
            title={''}
            groups={[]}
            category={null}
            shouldShowAlert={false}
            platform={null}
            appStoreType={props.appStoreType}
            releasedAt=""
            featureFlags={props.featureFlags}
            {...props}
        />
    );
};

export const MonitoringStatCard: React.FC<Omit<MonitoringStatCardBaseProps, 'isLoading'>> = (props) => {
    return <MonitoringStatCardBase {...props} />;
};

interface FeatureFlagPopoverProps {
    batchedDay?: string;
    datums: FeatureFlagTimeSeriesDatum[];
    onShowFeatureFlag: (flag: FeatureFlag) => void;
    onPopoverOpen?: () => void;
}

const FeatureFlagPopover: React.FC<FeatureFlagPopoverProps> = (props: FeatureFlagPopoverProps) => {
    const [popoverOpen, setPopoverOpen] = React.useState(false);

    return (
        <Popover
            open={popoverOpen}
            onOpenChange={(open) => {
                setPopoverOpen(open);
                if (open) {
                    props.onPopoverOpen?.();
                }
            }}
            arrow={false}
            title={
                props.batchedDay ?
                    <Typography.Title level={4}>
                        {dayjs.utc(props.batchedDay).format(DatetimeFormat.ShortMonthDateAppendix)} changes
                    </Typography.Title>
                :   null
            }
            content={
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                        maxWidth: 800,
                        height: 300,
                        overflowY: 'scroll',
                    }}
                >
                    {props.datums.map((featureFlagChangeDatum) => {
                        const featureFlag = featureFlagChangeDatum.flagAfter ?? featureFlagChangeDatum.flagBefore;
                        const flagName = featureFlag.name;

                        return (
                            <>
                                <div
                                    style={{
                                        gap: 4,
                                        alignItems: 'stretch',
                                        flexDirection: 'column',
                                        marginBottom: 12,
                                        marginTop: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'start',
                                        }}
                                    >
                                        <Typography.Title level={5}>Feature flag changes - {flagName}</Typography.Title>
                                        {featureFlagChangeDatum.flagAfter && (
                                            <Button
                                                onClick={() => {
                                                    setPopoverOpen(false);
                                                    props.onShowFeatureFlag(featureFlagChangeDatum.flagAfter);
                                                }}
                                                type="link"
                                                icon={<ArrowRightOutlined style={{ color: runwayColors.primary }} />}
                                            >
                                                View flag
                                            </Button>
                                        )}
                                    </div>
                                    <Typography.Text>
                                        Detected on:{' '}
                                        {dayjs
                                            .utc(featureFlagChangeDatum.timestamp)
                                            .local()
                                            .format(DatetimeFormat.ShortMonthDateTime)}
                                    </Typography.Text>
                                </div>
                                <Space direction={'vertical'}>
                                    {featureFlagChangeDatum.diff.map((change, i) => {
                                        return (
                                            <Descriptions
                                                key={`${i}`}
                                                size={'small'}
                                                column={2}
                                                items={[
                                                    {
                                                        key: 'changeType',
                                                        label: 'Change type',
                                                        children: <Typography.Text>{change.type}</Typography.Text>,
                                                    },
                                                    {
                                                        key: 'path',
                                                        label: 'Path',
                                                        children: (
                                                            <Typography.Text>{change.path.join('/')}</Typography.Text>
                                                        ),
                                                    },
                                                    {
                                                        key: 'from',
                                                        label: 'From',
                                                        children: (
                                                            <Typography.Paragraph
                                                                ellipsis={{ rows: 3, expandable: true }}
                                                            >
                                                                {change.from ? JSON.stringify(change.from) : 'None'}
                                                            </Typography.Paragraph>
                                                        ),
                                                    },
                                                    {
                                                        key: 'to',
                                                        label: 'To',
                                                        children: (
                                                            <Typography.Paragraph
                                                                ellipsis={{ rows: 3, expandable: true }}
                                                            >
                                                                {change.to ? JSON.stringify(change.to) : 'None'}
                                                            </Typography.Paragraph>
                                                        ),
                                                    },
                                                ]}
                                            ></Descriptions>
                                        );
                                    })}
                                </Space>
                            </>
                        );
                    })}
                </div>
            }
        >
            <FlagFilled style={{ fontSize: 12, color: runwayColors.primary }} />
        </Popover>
    );
};
